import React, { useState } from 'react';
import { 
  TextField, 
  Grid, 
  CircularProgress, 
  Alert, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Card,
  CardContent,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Transgender as GenderIcon,
  CalendarToday as CalendarIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { PrimaryButton } from '../ui/StyledComponents';
import supabase from '../../supabaseClient';

const initialState = {
  first_name: '',
  middle_name: '',
  last_name: '',
  date_of_birth: '',
  gender: '',
  email: '',
  phone: '',
};

function formatPhone(value) {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  // Format as (XXX) XXX-XXXX
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function CreateGuardianForm({ onSuccess }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      // Only allow digits, format as US phone
      const formatted = formatPhone(value);
      setForm((prev) => ({ ...prev, phone: formatted }));
      // Validate length
      const digits = value.replace(/\D/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        setPhoneError('Phone number must be 10 digits');
      } else {
        setPhoneError('');
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    // Basic validation
    if (!form.first_name || !form.last_name) {
      setError('First name and last name are required.');
      setLoading(false);
      return;
    }
    // Phone validation
    const digits = form.phone.replace(/\D/g, '');
    if (form.phone && digits.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      setLoading(false);
      return;
    }
    const { data: newGuardian, error } = await supabase.from('guardians').insert({
      first_name: form.first_name,
      middle_name: form.middle_name || null,
      last_name: form.last_name,
      date_of_birth: form.date_of_birth || null,
      gender: form.gender || null,
      email: form.email || null,
      phone: form.phone ? digits.toString() : null,
    }).select().single();
    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
      setForm(initialState);
      if (onSuccess) onSuccess(newGuardian?.id);
    }
    setLoading(false);
  };

  return (
    <Card
      sx={{
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
        borderRadius: 3,
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 30px rgba(0, 0, 0, 0.4)' 
            : '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            pb: 2,
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(52, 152, 219, 0.2)' 
                : 'rgba(52, 152, 219, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonAddIcon 
              sx={{ 
                fontSize: { xs: 20, sm: 24 }, 
                color: theme.palette.primary.main 
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                color: theme.palette.text.primary,
              }}
            >
              Create Guardian
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mt: 0.5,
              }}
            >
              Add a new guardian to the system
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Middle Name"
                name="middle_name"
                value={form.middle_name}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={form.date_of_birth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ position: 'relative' }}>
                <GenderIcon 
                  sx={{ 
                    position: 'absolute',
                    left: 14,
                    top: 18,
                    zIndex: 1,
                    color: theme.palette.text.secondary,
                    fontSize: '1.25rem',
                    pointerEvents: 'none',
                  }} 
                />
                <FormControl 
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      pl: 5,
                    },
                  }}
                >
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Gender
                  </InputLabel>
                  <Select
                    name="gender"
                    value={form.gender}
                    onChange={handleChange}
                    label="Gender"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    <MenuItem value="Male" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Male
                    </MenuItem>
                    <MenuItem value="Female" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Female
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                inputProps={{ maxLength: 14 }}
                error={!!phoneError}
                helperText={phoneError || ''}
                placeholder="(555) 555-5555"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '1.25rem',
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">Guardian created successfully!</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider 
                sx={{ 
                  my: 2,
                  borderColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.08)',
                }} 
              />
            </Grid>
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 2,
                  pt: 1,
                }}
              >
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '10px 24px', sm: '12px 32px' },
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: !loading
                      ? theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(52, 152, 219, 0.3)'
                        : '0 4px 12px rgba(52, 152, 219, 0.2)'
                      : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: !loading ? 'translateY(-2px)' : 'none',
                      boxShadow: !loading
                        ? theme.palette.mode === 'dark'
                          ? '0 6px 16px rgba(52, 152, 219, 0.4)'
                          : '0 6px 16px rgba(52, 152, 219, 0.3)'
                        : 'none',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={isMobile ? 20 : 24} color="inherit" />
                  ) : (
                    'Create Guardian'
                  )}
                </PrimaryButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
}

export default CreateGuardianForm; 